import { JobResponseModel, JobStatus } from "../Models/API/JobModel"
import { useAppState } from "./useReduxState"
import { FetchState, hasValue, getValue } from "../Models/Client/FetchState"
import { useEffect, useMemo } from "react"
import { useDispatch } from "react-redux"
import { getJobResponse } from "../Actions/API/Job/JobActions"
import { useJob } from "./useJob"
import { useDereference } from "./useDereference"
import { Result } from "../Models/Utility/Result"
import { DataAccess } from "../Models/Client/DataAccess"
import { APIAppAsyncOperationModel } from "../Models/API/APIAppAsyncOperationModel"

export function useJobResponse(
    appID: string | undefined,
    jobID: string | undefined,
    isPostProcessAvailable?: boolean,
    postProcessOperation?: APIAppAsyncOperationModel
): FetchState<JobResponseModel> {
    const dispatch = useDispatch()
    const jobFetch = useJob(appID, jobID)
    const jobOutputFetch = useAppState(state => {
        if (jobID === undefined) {
            return
        }

        return state.api.jobs.outputs[jobID]
    })
    const dataAccess = useMemo(
        () =>
            jobID === undefined ? DataAccess.owner : DataAccess.perJob(jobID),
        [jobID]
    )
    const responseFetch = useDereference(
        jobID === undefined ? undefined : getValue(jobOutputFetch)?.response,
        dataAccess
    )

    useEffect(() => {
        if (
            jobID === undefined ||
            jobOutputFetch !== undefined ||
            !hasValue(jobFetch) ||
            jobFetch.value.status !== JobStatus.COMPLETED
        ) {
            return
        }

        dispatch(getJobResponse(jobID))
    }, [dispatch, jobOutputFetch, jobID, jobFetch])

    // fetch response data at 4 sec interval if not present
    useEffect(() => {
        if (
            jobID === undefined ||
            !hasValue(jobOutputFetch) ||
            !hasValue(jobFetch) ||
            jobFetch.value.status !== JobStatus.COMPLETED
        ) {
            return
        }
        if (
            isPostProcessAvailable &&
            (jobOutputFetch.value.responseData === undefined ||
                jobOutputFetch.value.responseData === null) &&
            (postProcessOperation === undefined ||
                postProcessOperation.status !== "FAILED")
        ) {
            const timer = setTimeout(() => {
                dispatch(getJobResponse(jobID))
            }, 4000)
            return () => {
                clearTimeout(timer)
            }
        }
    }, [
        dispatch,
        isPostProcessAvailable,
        jobOutputFetch,
        jobID,
        jobFetch,
        postProcessOperation
    ])

    return useMemo(() => {
        if (!hasValue(jobOutputFetch)) {
            return jobOutputFetch
        }

        if (!hasValue(responseFetch)) {
            return responseFetch
        }

        return Result.ok({
            ...jobOutputFetch.value,
            response: responseFetch.value
        })
    }, [jobOutputFetch, responseFetch])
}
